import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { FiCheck, FiChevronLeft, FiChevronRight, FiTrash2 } from 'react-icons/fi';
import { AuthContext } from '../auth/AuthContext';
import { listLeaveCalendar, listLeaveAssignableUsers, createLeaveCalendar, acknowledgeLeave, deleteLeaveEntry } from '../api/leaveCalendarApi';
import { getCustomTeams } from '../api/configApi';
import { mergeTeamsForDepartment, filterAssignableUsersByTeam } from '../utils/assigneeTeamFilter';
import { DEFAULT_DEPARTMENT } from '../constants/departments';
import toast from 'react-hot-toast';

/** Calendar grid: ISO-style week starting Monday. */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_BAR_LANES = 3;
const MONTH_OPTIONS = [
  { value: 0, label: 'January' },
  { value: 1, label: 'February' },
  { value: 2, label: 'March' },
  { value: 3, label: 'April' },
  { value: 4, label: 'May' },
  { value: 5, label: 'June' },
  { value: 6, label: 'July' },
  { value: 7, label: 'August' },
  { value: 8, label: 'September' },
  { value: 9, label: 'October' },
  { value: 10, label: 'November' },
  { value: 11, label: 'December' },
];

const ACK_ROLES = new Set([
  'superadmin',
  'admin',
  'manager',
  'marketing_manager',
  'brand_manager',
  'product_manager',
  'lead',
  'product',
  'analyst',
  'hr',
]);

/** May log leave on behalf of another user (sick leave, etc.) */
const APPLY_LEAVE_FOR_OTHERS_ROLES = new Set(['superadmin', 'admin', 'manager', 'marketing_manager', 'product_manager']);

/** Same shape as IssueForm `toArray` for GET /users */
function toUsersArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}

/** Matches onboarding / UserManagement defaults; custom teams use a stable hash palette. */
const TEAM_COLOR_PRESETS = {
  backend: { bg: '#EAF6FD', border: '#1272B3' },
  frontend: { bg: '#fce7f3', border: '#9d174d' },
  qa: { bg: '#ffedd5', border: '#c2410c' },
  devops: { bg: '#e0e7ff', border: '#4338ca' },
  design: { bg: '#fae8ff', border: '#a21caf' },
  product: { bg: '#fef9c3', border: '#b45309' },
  'full stack': { bg: '#d1fae5', border: '#047857' },
  sales: { bg: '#ecfccb', border: '#4d7c0f' },
  marketing: { bg: '#cffafe', border: '#0e7490' },
  pr: { bg: '#e0f2fe', border: '#0369a1' },
  brand: { bg: '#fce7f3', border: '#be185d' },
  content: { bg: '#fef3c7', border: '#b45309' },
  it: { bg: '#e0e7ff', border: '#4338ca' },
  hr: { bg: '#d1fae5', border: '#047857' },
  management: { bg: '#ede9fe', border: '#6d28d9' },
  'mobile app': { bg: '#ecfdf5', border: '#059669' },
  business: { bg: '#f1f5f9', border: '#475569' },
};

const TEAM_HASH_PALETTE = [
  { bg: '#e0f2fe', border: '#0369a1' },
  { bg: '#fce7f3', border: '#be185d' },
  { bg: '#fef3c7', border: '#b45309' },
  { bg: '#ede9fe', border: '#6d28d9' },
  { bg: '#ccfbf1', border: '#0f766e' },
  { bg: '#fee2e2', border: '#b91c1c' },
  { bg: '#f3e8ff', border: '#7e22ce' },
  { bg: '#dcfce7', border: '#15803d' },
];

/** When `users.team` is empty, color bars by app role so Developer ≠ Admin. */
const ROLE_COLOR_PRESETS = {
  superadmin: { bg: '#ede9fe', border: '#6d28d9' },
  admin: { bg: '#e0e7ff', border: '#4338ca' },
  manager: { bg: '#ffe4e6', border: '#be123c' },
  developer: { bg: '#cffafe', border: '#0e7490' },
  tester: { bg: '#ffedd5', border: '#ea580c' },
  lead: { bg: '#f3e8ff', border: '#9333ea' },
  product: { bg: '#fef9c3', border: '#ca8a04' },
  product_manager: { bg: '#ecfccb', border: '#4d7c0f' },
  analyst: { bg: '#EAF6FD', border: '#2C63AE' },
  designer: { bg: '#fce7f3', border: '#db2777' },
  devops: { bg: '#e2e8f0', border: '#475569' },
  hr: { bg: '#d1fae5', border: '#047857' },
  member: { bg: '#f5f5f4', border: '#57534e' },
  sales_marketing: { bg: '#fed7aa', border: '#c2410c' },
  marketing_manager: { bg: '#ccfbf1', border: '#0f766e' },
  brand_manager: { bg: '#fce7f3', border: '#be185d' },
  pr_manager: { bg: '#e0f2fe', border: '#0369a1' },
  content_marketer: { bg: '#fef3c7', border: '#b45309' },
  marketing_designer: { bg: '#fce7f3', border: '#db2777' },
  trainee: { bg: '#f1f5f9', border: '#64748b' },
  it_support: { bg: '#e0e7ff', border: '#4338ca' },
  hr_recruiter: { bg: '#dcfce7', border: '#15803d' },
};

function canonicalTeamKey(team) {
  if (!team || typeof team !== 'string') return '';
  let s = team.trim().toLowerCase();
  s = s.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (s === 'testing' || s === 'test') return 'qa';
  return s;
}

function hashTeamString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

function formatRoleLegendLabel(roleLower) {
  if (!roleLower) return 'Unknown role';
  if (roleLower === 'product_manager') return 'Product manager';
  return roleLower.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Bar background / border: team first, else role, else neutral. */
function leaveEntryPalette(team, role) {
  const keyT = canonicalTeamKey(team);
  if (keyT && TEAM_COLOR_PRESETS[keyT]) return TEAM_COLOR_PRESETS[keyT];
  const rawTeam = team && String(team).trim();
  if (rawTeam) {
    return TEAM_HASH_PALETTE[hashTeamString(rawTeam) % TEAM_HASH_PALETTE.length];
  }
  const rk = String(role || '')
    .toLowerCase()
    .trim();
  if (rk && ROLE_COLOR_PRESETS[rk]) return ROLE_COLOR_PRESETS[rk];
  if (rk) {
    return TEAM_HASH_PALETTE[hashTeamString(`role:${rk}`) % TEAM_HASH_PALETTE.length];
  }
  return { bg: '#f1f5f9', border: '#64748b' };
}

function leaveBarColors(team, role, dayCount) {
  return { ...leaveEntryPalette(team, role), label: `${dayCount}d` };
}

function intersectionDayCount(entryStart, entryEnd, periodStart, periodEnd) {
  const s = entryStart > periodStart ? entryStart : periodStart;
  const e = entryEnd < periodEnd ? entryEnd : periodEnd;
  if (s > e) return 0;
  return dayjs(e).diff(dayjs(s), 'day') + 1;
}

/** Weekdays in [startIso, endIso] inclusive (aligns with leave day_count). */
function businessDaysInClosedRange(startIso, endIso) {
  let n = 0;
  let cur = dayjs(startIso);
  const end = dayjs(endIso);
  while (cur.isBefore(end, 'day') || cur.isSame(end, 'day')) {
    const wd = cur.day();
    if (wd !== 0 && wd !== 6) n += 1;
    cur = cur.add(1, 'day');
  }
  return n;
}

function intersectionBusinessDayCount(entryStart, entryEnd, periodStart, periodEnd) {
  const s = entryStart > periodStart ? entryStart : periodStart;
  const e = entryEnd < periodEnd ? entryEnd : periodEnd;
  if (s > e) return 0;
  return businessDaysInClosedRange(s, e);
}

/** Build calendar cells: leading pads + days + trailing pads to full weeks (Monday first). */
function buildMonthCells(anchor) {
  const m = dayjs(anchor).startOf('month');
  const firstDowSunday0 = m.day();
  const leadingPads = (firstDowSunday0 + 6) % 7;
  const daysInMonth = m.daysInMonth();
  const cells = [];
  for (let i = 0; i < leadingPads; i += 1) cells.push({ type: 'pad' });
  for (let d = 1; d <= daysInMonth; d += 1) {
    const date = m.date(d);
    cells.push({ type: 'day', date, iso: date.format('YYYY-MM-DD') });
  }
  while (cells.length % 7 !== 0) cells.push({ type: 'pad' });
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return { weeks, monthLabel: m.format('MMMM YYYY') };
}

/** Column indices in week (0–6) where this leave overlaps a real day in month */
function weekSegmentForEntry(week, entry) {
  let c0 = -1;
  let c1 = -1;
  for (let j = 0; j < 7; j += 1) {
    const cell = week[j];
    if (cell.type !== 'day') continue;
    const { iso } = cell;
    if (isWeekendIso(iso)) continue;
    if (entry.start_date <= iso && entry.end_date >= iso) {
      if (c0 < 0) c0 = j;
      c1 = j;
    }
  }
  if (c0 < 0) return null;
  return { entry, c0, c1 };
}

function segmentsConflict(a, b) {
  return !(a.c1 < b.c0 || a.c0 > b.c1);
}

function assignBarLanes(segments) {
  const sorted = [...segments].sort(
    (a, b) => a.c0 - b.c0 || new Date(a.entry.created_at) - new Date(b.entry.created_at),
  );
  const lanes = [[], [], [], [], []];
  const overflow = [];
  for (const seg of sorted) {
    let placed = false;
    for (let L = 0; L < MAX_BAR_LANES; L += 1) {
      const conflict = lanes[L].some((s) => segmentsConflict(s, seg));
      if (!conflict) {
        lanes[L].push(seg);
        placed = true;
        break;
      }
    }
    if (!placed) overflow.push(seg);
  }
  return { lanes, overflow };
}

function overflowCountForDay(overflowSegs, iso) {
  const set = new Set();
  for (const seg of overflowSegs) {
    if (seg.entry.start_date <= iso && seg.entry.end_date >= iso) {
      set.add(seg.entry.id);
    }
  }
  return set.size;
}

function yearOptions(centerYear) {
  const years = [];
  for (let y = centerYear - 5; y <= centerYear + 5; y += 1) {
    years.push(y);
  }
  return years;
}

function isWeekendIso(iso) {
  const d = dayjs(iso);
  const wd = d.day();
  return wd === 0 || wd === 6;
}

function canDeleteEntry(entry, myId, canAck) {
  if (canAck) return true;
  const mine = myId != null && String(entry.user_id) === String(myId);
  if (!mine) return false;
  if (entry.acknowledged_at) return false;
  const todayIso = dayjs().format('YYYY-MM-DD');
  return String(entry.end_date || '') >= todayIso;
}

function LeaveBarChunk({ seg, canAck, myId, ackMut, delMut }) {
  const e = seg.entry;
  const colors = leaveBarColors(e.team, e.role, e.day_count);
  const canDelete = canDeleteEntry(e, myId, canAck);
  const name = e.full_name || e.email || 'User';
  return (
    <div
      role="presentation"
      onClick={(ev) => ev.stopPropagation()}
      onKeyDown={(ev) => ev.stopPropagation()}
      style={{
        gridColumn: `${seg.c0 + 1} / ${seg.c1 + 2}`,
        fontSize: '0.66rem',
        lineHeight: 1.25,
        padding: '3px 6px',
        borderRadius: 4,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderLeftWidth: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        minHeight: 20,
        maxHeight: 22,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
        {name}
      </span>
      <span style={{ color: '#64748b', fontWeight: 700, flexShrink: 0 }}>{colors.label}</span>
      {e.reason ? (
        <Tooltip title={e.reason}>
          <span style={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90, flexShrink: 1 }}>
            {e.reason}
          </span>
        </Tooltip>
      ) : null}
      {e.acknowledged_at ? (
        <Tooltip title={e.acknowledger_name ? `Approved by ${e.acknowledger_name}` : 'Approved'}>
          <FiCheck style={{ color: '#059669', flexShrink: 0 }} size={12} aria-hidden />
        </Tooltip>
      ) : null}
      {canAck ? (
        <button
          type="button"
          onClick={(ev) => {
            ev.stopPropagation();
            ackMut.mutate({ id: e.id, acknowledged: !e.acknowledged_at });
          }}
          style={{
            cursor: 'pointer',
            fontSize: '0.62rem',
            fontWeight: 700,
            padding: '0 4px',
            borderRadius: 3,
            border: '1px solid #cbd5e1',
            background: '#fff',
            color: '#475569',
            flexShrink: 0,
            lineHeight: 1.2,
          }}
        >
          {e.acknowledged_at ? '×' : '✓'}
        </button>
      ) : null}
      {canDelete && (
        <button
          type="button"
          onClick={(ev) => {
            ev.stopPropagation();
            delMut.mutate(e.id);
          }}
          style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, display: 'flex' }}
          title="Remove"
        >
          <FiTrash2 size={11} color="#b91c1c" />
        </button>
      )}
    </div>
  );
}

export default function TeamLeaveCalendar() {
  const { user, role } = useContext(AuthContext);
  const qc = useQueryClient();
  const r = String(role || '').toLowerCase();
  const canAck = ACK_ROLES.has(r);
  const myId = user?.id ?? user?.userId;
  const canApplyForOthers = APPLY_LEAVE_FOR_OTHERS_ROLES.has(r);

  const [anchor, setAnchor] = useState(() => dayjs());
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyStart, setApplyStart] = useState('');
  const [applyDays, setApplyDays] = useState('1');
  const [applyReason, setApplyReason] = useState('');
  const [applyTargetUserId, setApplyTargetUserId] = useState('');
  const [applyTeamFilter, setApplyTeamFilter] = useState('Any');
  const [stackDialog, setStackDialog] = useState(null);

  const calStart = dayjs(anchor).startOf('month').format('YYYY-MM-DD');
  const calEnd = dayjs(anchor).endOf('month').format('YYYY-MM-DD');
  const yearStart = dayjs(anchor).startOf('year').format('YYYY-MM-DD');
  const yearEnd = dayjs(anchor).endOf('year').format('YYYY-MM-DD');
  const leaderboardYearLabel = String(dayjs(anchor).year());

  const { data, isLoading } = useQuery({
    queryKey: ['leave-calendar', yearStart, yearEnd],
    queryFn: () => listLeaveCalendar(yearStart, yearEnd),
  });

  const userDepartment = user?.department || DEFAULT_DEPARTMENT;

  const { data: usersData, isPending: rosterLoading } = useQuery({
    queryKey: ['leave-assignable-users', userDepartment],
    queryFn: () => listLeaveAssignableUsers(),
    enabled: applyOpen && canApplyForOthers,
    staleTime: 60_000,
  });

  const { data: customTeams = [] } = useQuery({
    queryKey: ['custom-teams', userDepartment],
    queryFn: getCustomTeams,
    enabled: applyOpen && canApplyForOthers,
  });

  const allTeams = useMemo(
    () => mergeTeamsForDepartment(customTeams, userDepartment),
    [customTeams, userDepartment],
  );

  const usersList = useMemo(() => toUsersArray(usersData), [usersData]);

  const baseAssignableUsers = useMemo(() => {
    return usersList.filter((u) => {
      const rl = String(u.role || '').toLowerCase();
      if (['admin', 'superadmin'].includes(rl)) {
        return myId != null && String(u.id) === String(myId);
      }
      return true;
    });
  }, [usersList, myId]);

  const filteredAssignableUsers = useMemo(
    () => filterAssignableUsersByTeam(baseAssignableUsers, applyTeamFilter || 'Any', allTeams),
    [baseAssignableUsers, applyTeamFilter, allTeams],
  );

  useEffect(() => {
    if (!applyOpen || !canApplyForOthers || rosterLoading) return;
    if (baseAssignableUsers.length === 0) return;
    if (filteredAssignableUsers.length === 0 && applyTeamFilter !== 'Any') {
      setApplyTeamFilter('Any');
    }
  }, [applyOpen, canApplyForOthers, rosterLoading, baseAssignableUsers.length, filteredAssignableUsers.length, applyTeamFilter]);

  useEffect(() => {
    if (!applyOpen || !canApplyForOthers || rosterLoading || filteredAssignableUsers.length === 0) return;
    const ok = filteredAssignableUsers.some((u) => String(u.id) === applyTargetUserId);
    if (!ok) {
      const selfIn = myId != null && filteredAssignableUsers.some((u) => String(u.id) === String(myId));
      if (selfIn) setApplyTargetUserId(String(myId));
      else setApplyTargetUserId(String(filteredAssignableUsers[0].id));
    }
  }, [applyOpen, canApplyForOthers, rosterLoading, filteredAssignableUsers, applyTargetUserId, myId]);

  const entries = useMemo(() => data?.entries || [], [data?.entries]);

  const leaderboard = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      const n = intersectionBusinessDayCount(e.start_date, e.end_date, yearStart, yearEnd);
      if (n <= 0) continue;
      const key = e.user_id;
      const name = e.full_name || e.email || `User ${key}`;
      const prev = map.get(key) || { user_id: key, name, days: 0, team: e.team || null, role: e.role || null };
      prev.days += n;
      prev.name = name;
      if (e.team) prev.team = e.team;
      if (e.role) prev.role = e.role;
      map.set(key, prev);
    }
    return [...map.values()].sort((a, b) => b.days - a.days);
  }, [entries, yearStart, yearEnd]);

  const { weeks, monthLabel } = useMemo(() => buildMonthCells(anchor), [anchor]);

  const colorLegendItems = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (intersectionDayCount(e.start_date, e.end_date, calStart, calEnd) <= 0) continue;
      const teamTrim = (e.team && String(e.team).trim()) || '';
      const roleLower = String(e.role || '')
        .toLowerCase()
        .trim();
      let key;
      let displayLabel;
      if (teamTrim) {
        key = `team:${canonicalTeamKey(teamTrim) || teamTrim.toLowerCase()}`;
        displayLabel = teamTrim;
      } else {
        key = `role:${roleLower || 'unknown'}`;
        displayLabel = formatRoleLegendLabel(roleLower);
      }
      if (!m.has(key)) {
        m.set(key, { key, displayLabel, team: teamTrim || null, role: teamTrim ? null : roleLower || null });
      }
    }
    return [...m.values()].sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }, [entries, calStart, calEnd]);

  const weekLayouts = useMemo(() => {
    return weeks.map((week) => {
      const segments = [];
      for (const e of entries) {
        const seg = weekSegmentForEntry(week, e);
        if (seg) segments.push(seg);
      }
      return assignBarLanes(segments);
    });
  }, [weeks, entries]);

  const createMut = useMutation({
    mutationFn: createLeaveCalendar,
    onSuccess: () => {
      toast.success('Leave added to calendar');
      qc.invalidateQueries({ queryKey: ['leave-calendar'] });
      setApplyOpen(false);
      setApplyReason('');
      setApplyDays('1');
      if (myId != null) setApplyTargetUserId(String(myId));
      setApplyTeamFilter('Any');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not save'),
  });

  const ackMut = useMutation({
    mutationFn: ({ id, acknowledged }) => acknowledgeLeave(id, acknowledged),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave-calendar'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Update failed'),
  });

  const delMut = useMutation({
    mutationFn: deleteLeaveEntry,
    onSuccess: () => {
      toast.success('Removed');
      qc.invalidateQueries({ queryKey: ['leave-calendar'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Delete failed'),
  });

  const openApply = (iso) => {
    setApplyStart(iso);
    setApplyDays('1');
    setApplyReason('');
    setApplyTeamFilter('Any');
    if (myId != null) setApplyTargetUserId(String(myId));
    setApplyOpen(true);
  };

  const selectedMonth = dayjs(anchor).month();
  const selectedYear = dayjs(anchor).year();
  const years = useMemo(() => yearOptions(dayjs().year()), []);

  const openStackForDay = (iso, weekIndex) => {
    const onDay = entries
      .filter((e) => e.start_date <= iso && e.end_date >= iso)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const overflowSegs = weekLayouts[weekIndex]?.overflow || [];
    const overflowOnDay = overflowSegs
      .filter((s) => s.entry.start_date <= iso && s.entry.end_date >= iso)
      .map((s) => s.entry);
    const merged = [];
    const seen = new Set();
    for (const e of [...onDay, ...overflowOnDay]) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      merged.push(e);
    }
    setStackDialog({ iso, entries: merged });
  };

  return (
    <div style={{ padding: '1.25rem 1.5rem', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: '#0f172a' }}>Team leave calendar</h1>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.9rem', maxWidth: 720 }}>
          See who is away and plan work. Click a date to add leave.
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
          padding: '12px 14px',
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconButton aria-label="Previous month" onClick={() => setAnchor((a) => dayjs(a).subtract(1, 'month'))} size="small">
            <FiChevronLeft />
          </IconButton>
          <span style={{ fontWeight: 800, color: '#1e293b', minWidth: 140, textAlign: 'center' }}>{monthLabel}</span>
          <IconButton aria-label="Next month" onClick={() => setAnchor((a) => dayjs(a).add(1, 'month'))} size="small">
            <FiChevronRight />
          </IconButton>
        </div>
        <TextField
          select
          size="small"
          label="Month"
          value={selectedMonth}
          onChange={(e) => {
            const nextMonth = parseInt(e.target.value, 10);
            setAnchor((cur) => dayjs(cur).month(nextMonth).startOf('month'));
          }}
          sx={{ minWidth: 170 }}
        >
          {MONTH_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Year"
          value={selectedYear}
          onChange={(e) => {
            const nextYear = parseInt(e.target.value, 10);
            setAnchor((cur) => dayjs(cur).year(nextYear).startOf('month'));
          }}
          sx={{ minWidth: 120 }}
        >
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
        {isLoading ? <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</span> : null}
      </div>

      {colorLegendItems.length > 0 ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '10px 16px',
            marginBottom: 16,
            padding: '10px 14px',
            background: '#f8fafc',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
          }}
        >
          <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Colors
          </span>
          {colorLegendItems.map((item) => {
            const c = leaveEntryPalette(item.team, item.role);
            return (
              <span key={item.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#334155' }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: c.bg,
                    border: `2px solid ${c.border}`,
                    flexShrink: 0,
                  }}
                  aria-hidden
                />
                {item.displayLabel}
              </span>
            );
          })}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'stretch' }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 16, overflow: 'auto' }}>
          <div style={{ fontWeight: 800, marginBottom: 12, color: '#334155' }}>{monthLabel}</div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
            {WEEKDAYS.map((w) => (
              <div key={w} style={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', padding: 4 }}>
                {w}
              </div>
            ))}
          </div>

          {weeks.map((week, wi) => {
            const { lanes, overflow } = weekLayouts[wi] || { lanes: [[], [], [], [], []], overflow: [] };
            return (
              <div key={wi} style={{ marginBottom: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                  {week.map((cell, idx) => {
                    if (cell.type === 'pad') {
                      return <div key={`pad-${wi}-${idx}`} style={{ minHeight: 44 }} />;
                    }
                    const { iso } = cell;
                    const isToday = iso === dayjs().format('YYYY-MM-DD');
                    const weekend = isWeekendIso(iso);
                    const overflowN = overflowCountForDay(overflow, iso);
                    const totalOnDay = entries.filter((e) => e.start_date <= iso && e.end_date >= iso).length;
                    const extra = Math.max(overflowN, Math.max(0, totalOnDay - MAX_BAR_LANES));
                    return (
                      <button
                        key={iso}
                        type="button"
                        onClick={() => {
                          if (!weekend) openApply(iso);
                        }}
                        style={{
                          minHeight: 44,
                          textAlign: 'left',
                          padding: '6px 6px 4px',
                          borderRadius: 8,
                          border: isToday ? '2px solid #2C8FC7' : weekend ? '1px solid #86efac' : '1px solid #e2e8f0',
                          background: weekend ? '#f0fdf4' : '#fafafa',
                          cursor: weekend ? 'default' : 'pointer',
                          position: 'relative',
                        }}
                      >
                        <div style={{ fontSize: '0.75rem', fontWeight: 800, color: weekend ? '#15803d' : '#475569' }}>
                          {cell.date.date()}
                        </div>
                        {weekend ? (
                          <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#15803d', marginTop: 4 }}>Holiday</div>
                        ) : null}
                        {totalOnDay > 0 ? (
                          <button
                            type="button"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              openStackForDay(iso, wi);
                            }}
                            style={{
                              marginTop: 4,
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              padding: '2px 6px',
                              borderRadius: 6,
                              border: extra > 0 ? '1px solid #c4b5fd' : '1px solid #cbd5e1',
                              background: extra > 0 ? '#f5f3ff' : '#f8fafc',
                              color: extra > 0 ? '#5b21b6' : '#475569',
                              cursor: 'pointer',
                            }}
                          >
                            {extra > 0 ? `+${extra} more` : `Open (${totalOnDay})`}
                          </button>
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                {lanes.some((laneSegs) => laneSegs.length > 0) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                    {lanes
                      .filter((laneSegs) => laneSegs.length > 0)
                      .map((laneSegs, li) => (
                    <div
                      key={li}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: 4,
                        minHeight: 18,
                        alignItems: 'stretch',
                      }}
                    >
                      {laneSegs.map((seg) => (
                        <LeaveBarChunk
                          key={`${seg.entry.id}-${wi}-${li}`}
                          seg={seg}
                          canAck={canAck}
                          myId={myId}
                          ackMut={ackMut}
                          delMut={delMut}
                        />
                      ))}
                    </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <div style={{ fontWeight: 800, color: '#334155', marginBottom: 10 }}>Leave leaderboard</div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: 10 }}>
            Weekday leave days in {leaderboardYearLabel}
          </div>
          {leaderboard.length === 0 ? (
            <div style={{ color: '#cbd5e1', fontSize: '0.85rem' }}>No leave recorded this year.</div>
          ) : (
            <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {leaderboard.map((row, i) => {
                const lbColors = leaveBarColors(row.team, row.role, row.days);
                return (
                <li
                  key={row.user_id}
                  style={{ fontSize: '0.85rem', color: '#334155', display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span style={{ color: '#0f172a', fontWeight: 800 }}>#{i + 1}</span>
                  <span
                    title={row.team ? `Team: ${row.team}` : row.role ? `Role: ${formatRoleLegendLabel(row.role)}` : undefined}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      flexShrink: 0,
                      background: lbColors.bg,
                      border: `2px solid ${lbColors.border}`,
                    }}
                    aria-hidden
                  />
                  <span style={{ color: '#0f172a', fontWeight: 700, flex: 1, minWidth: 0 }}>{row.name}</span>
                  <span style={{ color: '#64748b', fontWeight: 800, flexShrink: 0 }}>{row.days}d</span>
                </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>

      <Dialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { zIndex: 1400 } } }}
      >
        <DialogTitle>Apply leave</DialogTitle>
        <DialogContent>
          {canApplyForOthers ? (
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: '#64748b' }}>
                Same team and person filters as task assignment, scoped to your department. Defaults to you — change only when
                recording leave for someone else (e.g. sick leave).
              </p>
              {rosterLoading ? (
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>Loading people…</p>
              ) : baseAssignableUsers.length === 0 ? (
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#b91c1c' }}>
                  No approved users found. Contact an administrator.
                </p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                  <FormControl size="small" sx={{ flex: '1 1 160px', minWidth: 160 }}>
                    <InputLabel id="leave-apply-team">Assign to team</InputLabel>
                    <Select
                      labelId="leave-apply-team"
                      label="Assign to team"
                      value={applyTeamFilter}
                      onChange={(e) => {
                        setApplyTeamFilter(e.target.value);
                        setApplyTargetUserId('');
                      }}
                      MenuProps={{ PaperProps: { sx: { zIndex: 2000 } } }}
                    >
                      {allTeams.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: '1 1 220px', minWidth: 200 }}>
                    <InputLabel id="leave-apply-name">Assign to person</InputLabel>
                    <Select
                      labelId="leave-apply-name"
                      label="Assign to person"
                      value={applyTargetUserId}
                      onChange={(e) => setApplyTargetUserId(e.target.value)}
                      MenuProps={{ PaperProps: { sx: { zIndex: 2000 } } }}
                    >
                      <MenuItem value="">
                        {filteredAssignableUsers.length === 0 ? 'No people in this team' : 'Select person…'}
                      </MenuItem>
                      {filteredAssignableUsers.map((u) => {
                        const name = u.full_name || u.email || `User ${u.id}`;
                        return (
                          <MenuItem key={u.id} value={String(u.id)}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {applyTeamFilter !== 'Any' && filteredAssignableUsers.length === 0 ? (
                    <p style={{ margin: 0, width: '100%', fontSize: '0.72rem', color: '#d97706', fontWeight: 600 }}>
                      No members on {applyTeamFilter} yet (same as task assign).
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
          <TextField
            margin="dense"
            label="Start date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={applyStart}
            onChange={(e) => setApplyStart(e.target.value)}
            sx={{ mb: 2, mt: canApplyForOthers ? 0 : 1 }}
          />
          <TextField
            margin="dense"
            label="Number of leave days (weekdays)"
            type="number"
            fullWidth
            inputProps={{ min: 1, max: 366 }}
            value={applyDays}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setApplyDays(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            value={applyReason}
            onChange={(e) => setApplyReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !applyStart ||
              createMut.isPending ||
              !Number.isFinite(parseInt(applyDays, 10)) ||
              parseInt(applyDays, 10) < 1 ||
              (canApplyForOthers && (!applyTargetUserId || !Number.isFinite(parseInt(applyTargetUserId, 10))))
            }
            onClick={() => {
              const body = {
                start_date: applyStart,
                day_count: parseInt(applyDays, 10),
                reason: applyReason,
              };
              if (canApplyForOthers && applyTargetUserId) {
                body.for_user_id = parseInt(applyTargetUserId, 10);
              }
              createMut.mutate(body);
            }}
          >
            {createMut.isPending ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!stackDialog}
        onClose={() => setStackDialog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <div style={{ fontWeight: 800, color: '#0f172a' }}>
            Leave on {stackDialog?.iso ? dayjs(stackDialog.iso).format('dddd, MMM D, YYYY') : ''}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500, marginTop: 2 }}>
            {(stackDialog?.entries || []).length} {(stackDialog?.entries || []).length === 1 ? 'person' : 'people'} away
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {(stackDialog?.entries || []).length === 0 ? (
            <p style={{ color: '#94a3b8' }}>No entries.</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {stackDialog.entries.map((e) => {
                const colors = leaveBarColors(e.team, e.role, e.day_count);
                const canDelete = canDeleteEntry(e, myId, canAck);
                return (
                  <li
                    key={e.id}
                    style={{
                      padding: '12px 14px',
                      borderRadius: 10,
                      border: `1px solid ${colors.border}`,
                      borderLeftWidth: 4,
                      background: colors.bg,
                      fontSize: '0.85rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220, flex: '1 1 260px' }}>
                        <span style={{ fontWeight: 800, color: '#0f172a', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {e.full_name || e.email}
                          {e.acknowledged_at ? (
                            <Tooltip title={e.acknowledger_name ? `Approved by ${e.acknowledger_name}` : 'Approved'}>
                              <span style={{ display: 'inline-flex' }}>
                                <FiCheck style={{ color: '#059669' }} />
                              </span>
                            </Tooltip>
                          ) : null}
                        </span>
                        <span style={{ color: '#64748b', fontSize: '0.78rem', marginTop: 2 }}>
                          {e.team ? `${e.team} · ` : ''}{e.start_date} → {e.end_date} · {e.day_count}d
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
                        {canAck && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => ackMut.mutate({ id: e.id, acknowledged: !e.acknowledged_at })}
                          >
                            {e.acknowledged_at ? 'Clear approval' : 'Approve leave'}
                          </Button>
                        )}
                        {canDelete && (
                          <Button size="small" color="error" variant="outlined" onClick={() => delMut.mutate(e.id)}>
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                    {e.reason ? (
                      <div style={{ marginTop: 8, color: '#334155', whiteSpace: 'pre-wrap' }}>{e.reason}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStackDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
